import { Heading } from '@/components/ui/heading'
import { Spinner } from '@/components/ui/spinner'
import { selectTableCoversMetaState, type TableCoversFetchingKey } from '@/redux/slices/tableCoverSlice'
import { useAppSelector } from '@/redux/store'
import { BaseFileEnum } from '@/typings/domain'
import { identity } from 'lodash-es'
import { useCallback, useMemo, useState } from 'react'
import { RefreshControl, SectionList, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import useCustomTabBarHeight from '@/lib/hooks/useCustomTabBarHeight'
import TableCover from './TableCover'
import TableCoverListFooter from './TableCoverListFooter'

type TableCoverListProps = TableCoversFetchingKey & {
  includeFolder: boolean
  initialLoading?: boolean
  onLoadMore: (type: BaseFileEnum) => void
  onRefresh: () => void
  ListHeaderComponent?: React.ComponentType | React.ReactElement
}

export default function TableCoverList({
  includeFolder,
  initialLoading,
  onLoadMore,
  onRefresh,
  ListHeaderComponent,
  ...fetchingKey
}: TableCoverListProps) {
  const insets = useSafeAreaInsets()
  const customTabBarHeight = useCustomTabBarHeight()
  const isKeywordSearch = 'byKeyword' in fetchingKey && fetchingKey.byKeyword
  const tableCoverIds = useAppSelector(
    state => selectTableCoversMetaState(state, fetchingKey, BaseFileEnum.Form).coverIds
  )
  const folderCoverIds = useAppSelector(
    state => selectTableCoversMetaState(state, fetchingKey, BaseFileEnum.FormFolder).coverIds
  )
  const tableLoading = useAppSelector(
    state => selectTableCoversMetaState(state, fetchingKey, BaseFileEnum.Form).isLoading
  )
  const tableHasNextPage = useAppSelector(
    state => selectTableCoversMetaState(state, fetchingKey, BaseFileEnum.Form).pageInfo.hasNextPage
  )
  const folderLoading = useAppSelector(
    state => selectTableCoversMetaState(state, fetchingKey, BaseFileEnum.FormFolder).isLoading
  )
  const folderHasNextPage = useAppSelector(
    state => selectTableCoversMetaState(state, fetchingKey, BaseFileEnum.FormFolder).pageInfo.hasNextPage
  )

  const [refreshing, setRefreshing] = useState(false)

  const isEmpty = tableCoverIds.length === 0 && folderCoverIds.length === 0
  const foldersStillPaging = includeFolder && folderHasNextPage

  const sections = useMemo(() => {
    const res = []
    if (includeFolder && folderCoverIds.length > 0) {
      res.push({ title: '文件夹', data: folderCoverIds })
    }
    if (tableCoverIds.length > 0 && !foldersStillPaging) {
      res.push({ title: '表格', data: tableCoverIds })
    }
    return res
  }, [folderCoverIds, tableCoverIds, includeFolder, foldersStillPaging])

  const renderItem = useCallback(
    ({ item: coverId, index, section }: { item: string; index: number; section: { data: string[] } }) => (
      <TableCover id={coverId} showBorder={index < section.data.length - 1} />
    ),
    []
  )

  const renderSectionHeader = useCallback(
    ({ section }: { section: { title: string } }) => {
      return includeFolder && folderCoverIds.length > 0 ? (
        <Heading size="md" className="px-6 pt-2 pb-1 text-secondary-label">
          {section.title}
        </Heading>
      ) : null
    },
    [includeFolder, folderCoverIds.length]
  )

  const handleRefresh = useCallback(() => {
    setRefreshing(true)
    onRefresh()
    setRefreshing(false)
  }, [onRefresh])

  const handleLoadMore = useCallback(() => {
    if (isEmpty) return
    if (foldersStillPaging) {
      if (folderLoading) return
      onLoadMore(BaseFileEnum.FormFolder)
      return
    }
    if (tableLoading || !tableHasNextPage) return
    onLoadMore(BaseFileEnum.Form)
  }, [isEmpty, foldersStillPaging, folderLoading, tableLoading, tableHasNextPage, onLoadMore])

  return (
    <SectionList
      sections={sections}
      renderItem={renderItem}
      renderSectionHeader={renderSectionHeader}
      ListHeaderComponent={ListHeaderComponent}
      ListEmptyComponent={
        initialLoading ? (
          <View style={{ height: 300, justifyContent: 'center', alignItems: 'center' }}>
            <Spinner />
          </View>
        ) : undefined
      }
      stickySectionHeadersEnabled={false}
      keyExtractor={identity}
      className="bg-background-0"
      style={{ flex: 1 }}
      contentInsetAdjustmentBehavior="automatic"
      contentContainerStyle={{ paddingBottom: insets.bottom + customTabBarHeight + 16 }}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />}
      onEndReached={handleLoadMore}
      onEndReachedThreshold={0.1}
      ListFooterComponent={() => (
        <TableCoverListFooter
          context={isKeywordSearch ? 'search' : fetchingKey.filter}
          empty={isEmpty && !initialLoading}
          loading={tableLoading && !initialLoading}
        />
      )}
    />
  )
}
